import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaSync, FaExclamationCircle, FaPlus, FaEdit, FaTrash, FaSave, FaTimes, FaQuestion } from 'react-icons/fa';
import { SubTabsContainer, SubTab } from './common/AdminTabs';

// Styled components
const PageContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
`;

const Container = styled.div`
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  padding: 24px;
  margin-bottom: 20px;
  width: 100%;
  overflow-x: hidden;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const Title = styled.h2`
  font-size: 20px;
  color: #333;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
  width: 100%;
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: #1890ff;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.3s;

  &:hover {
    background-color: #40a9ff;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #8c8c8c;
  gap: 16px;

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const Alert = styled.div`
  background-color: ${props => props.type === 'error' ? '#fff2f0' : '#f6ffed'};
  border: 1px solid ${props => props.type === 'error' ? '#ffccc7' : '#b7eb8f'};
  border-radius: 4px;
  padding: 8px 16px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  color: ${props => props.type === 'error' ? '#cf1322' : '#389e0d'};
`;

const AlertIcon = styled.span`
  margin-right: 8px;
  display: flex;
  align-items: center;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #8c8c8c;
  gap: 16px;
`;

const EmptyStateText = styled.p`
  margin: 0;
  font-size: 16px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHead = styled.thead`
  background-color: #fafafa;
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  border-bottom: 1px solid #f0f0f0;
  background-color: ${props => props.highlighted ? '#e6f7ff' : 'transparent'};
  
  &:hover {
    background-color: #fafafa;
  }
`;

const TableHeader = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-weight: 500;
  color: #262626;
  font-size: 14px;
`;

const TableCell = styled.td`
  padding: 12px 16px;
  font-size: 14px;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 14px;
  
  &:focus {
    border-color: #40a9ff;
    outline: none;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 14px;
  min-height: 80px;
  resize: vertical;
  
  &:focus {
    border-color: #40a9ff;
    outline: none;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${props => props.danger ? '#ff4d4f' : '#1890ff'};
  font-size: 16px;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: ${props => props.danger ? '#fff1f0' : '#e6f7ff'};
  }
`;

const Modal = ({ show, onClose, title, children }) => {
  if (!show) return null;
  
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '4px',
        width: '500px',
        padding: '20px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ margin: 0 }}>{title}</h3>
          <button 
            onClick={onClose} 
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px' }}
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

const QuestionsTab = () => {
  const [questions, setQuestions] = useState([
    { id: 1, question: 'What is AI Governance?', answer: 'AI Governance refers to the frameworks, policies, and practices designed to ensure that AI systems are developed and used ethically, safely, and in compliance with regulations.' },
    { id: 2, question: 'How do I set up my API key?', answer: 'You can set up your API key in the AI Models tab under your user settings. Click on "AI Preferences" and enter your API key in the designated field.' },
    { id: 3, question: 'What models are supported?', answer: 'We currently support OpenAI models (GPT-4, GPT-3.5) and Ollama models. You can configure your preferred model in the AI Models tab.' }
  ]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [newQuestion, setNewQuestion] = useState({ question: '', answer: '' });
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [activeSubTab, setActiveSubTab] = useState('all');

  const handleAddQuestion = () => {
    setShowAddModal(true);
    setNewQuestion({ question: '', answer: '' });
  };

  const handleSaveNewQuestion = () => {
    if (!newQuestion.question || !newQuestion.answer) {
      setError('Both question and answer are required');
      return;
    }

    const newId = questions.length > 0 ? Math.max(...questions.map(q => q.id)) + 1 : 1;
    setQuestions([...questions, { ...newQuestion, id: newId }]);
    setShowAddModal(false);
    setNewQuestion({ question: '', answer: '' });
  };

  const handleEditStart = (index) => {
    setEditingIndex(index);
    setEditingQuestion({ ...questions[index] });
  };

  const handleEditCancel = () => {
    setEditingIndex(null);
    setEditingQuestion(null);
  };

  const handleEditChange = (field, value) => {
    setEditingQuestion(prev => ({ ...prev, [field]: value }));
  };

  const handleEditSave = (index) => {
    if (!editingQuestion.question || !editingQuestion.answer) {
      setError('Both question and answer are required');
      return;
    }

    const updatedQuestions = [...questions];
    updatedQuestions[index] = editingQuestion;
    setQuestions(updatedQuestions);
    setEditingIndex(null);
    setEditingQuestion(null);
  };

  const handleDeleteQuestion = (id) => {
    if (window.confirm('Are you sure you want to delete this question?')) {
      setQuestions(questions.filter(q => q.id !== id));
    }
  };

  return (
    <PageContainer>
      <Container>
        <Title>Frequently Asked Questions</Title>
        
        <SubTabsContainer>
          <SubTab 
            active={activeSubTab === 'all'} 
            onClick={() => setActiveSubTab('all')}
          >
            <FaQuestion style={{ marginRight: '5px' }} />
            All Questions
          </SubTab>
          <SubTab 
            active={activeSubTab === 'recent'} 
            onClick={() => setActiveSubTab('recent')}
          >
            Recent Questions
          </SubTab>
          <SubTab 
            active={activeSubTab === 'popular'} 
            onClick={() => setActiveSubTab('popular')}
          >
            Popular Questions
          </SubTab>
        </SubTabsContainer>
        
        <Header>
          <div></div> {/* Empty div to maintain flex spacing */}
          <AddButton onClick={handleAddQuestion}>
            <FaPlus />
            Add Question
          </AddButton>
        </Header>

        {loading ? (
          <LoadingContainer>
            <FaSync className="spin" />
            <span>Loading questions...</span>
          </LoadingContainer>
        ) : error ? (
          <Alert type="error">
            <AlertIcon><FaExclamationCircle /></AlertIcon>
            {error}
          </Alert>
        ) : questions.length === 0 ? (
          <EmptyState>
            <EmptyStateText>No questions found. Add your first question.</EmptyStateText>
          </EmptyState>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableHeader>Question</TableHeader>
                <TableHeader>Answer</TableHeader>
                <TableHeader>Actions</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {questions.map((q, idx) => {
                const isEditing = editingIndex === idx;
                
                return (
                  <TableRow key={q.id}>
                    <TableCell>
                      {isEditing ? (
                        <Input
                          value={editingQuestion.question}
                          onChange={(e) => handleEditChange('question', e.target.value)}
                        />
                      ) : (
                        q.question
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <TextArea
                          value={editingQuestion.answer}
                          onChange={(e) => handleEditChange('answer', e.target.value)}
                        />
                      ) : (
                        q.answer.length > 100 ? `${q.answer.substring(0, 100)}...` : q.answer
                      )}
                    </TableCell>
                    <TableCell>
                      <ActionButtons>
                        {isEditing ? (
                          <>
                            <ActionButton onClick={() => handleEditSave(idx)} title="Save">
                              <FaSave />
                            </ActionButton>
                            <ActionButton onClick={handleEditCancel} title="Cancel">
                              <FaTimes />
                            </ActionButton>
                          </>
                        ) : (
                          <>
                            <ActionButton onClick={() => handleEditStart(idx)} title="Edit question">
                              <FaEdit />
                            </ActionButton>
                            <ActionButton 
                              onClick={() => handleDeleteQuestion(q.id)} 
                              title="Delete question"
                              danger
                            >
                              <FaTrash />
                            </ActionButton>
                          </>
                        )}
                      </ActionButtons>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        <Modal 
          show={showAddModal} 
          onClose={() => setShowAddModal(false)}
          title="Add New Question"
        >
          {error && (
            <Alert type="error">
              <AlertIcon><FaExclamationCircle /></AlertIcon>
              {error}
            </Alert>
          )}
          
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Question</label>
            <Input 
              value={newQuestion.question} 
              onChange={(e) => setNewQuestion({...newQuestion, question: e.target.value})} 
              placeholder="Enter question" 
            />
          </div>
          
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Answer</label>
            <TextArea 
              value={newQuestion.answer} 
              onChange={(e) => setNewQuestion({...newQuestion, answer: e.target.value})} 
              placeholder="Enter answer" 
            />
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <button 
              onClick={() => setShowAddModal(false)}
              style={{
                padding: '8px 16px',
                border: '1px solid #d9d9d9',
                borderRadius: '4px',
                background: 'white',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button 
              onClick={handleSaveNewQuestion}
              style={{
                padding: '8px 16px',
                background: '#1890ff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Save
            </button>
          </div>
        </Modal>
      </Container>
    </PageContainer>
  );
};

export default QuestionsTab;
